import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CaptchaState, FormErrors } from '../../../../types/ContactForm';
import './CaptchaField.scss';

interface CaptchaFieldProps {
  captcha: CaptchaState;
  errors: FormErrors;
  onCaptchaChange: (result: string) => void;
  captchaType?: string;
  apiUrl?: string;
  onRecaptchaReady?: (ready: boolean) => void;
}

export const CaptchaField: React.FC<CaptchaFieldProps> = ({
  captcha,
  errors,
  onCaptchaChange,
  captchaType = 'math',
  apiUrl,
  onRecaptchaReady
}) => {
  const { t } = useTranslation('form');
  const [captchaMarkup, setCaptchaMarkup] = useState<string>('');
  const [siteKey, setSiteKey] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleRecaptchaApiLoaded, setIsGoogleRecaptchaApiLoaded] = useState(false);

  useEffect(() => {
    if (captchaType === 'google_recaptcha') {
      loadGoogleRecaptcha();
      if (typeof (window as any).CM !== 'undefined' && (window as any).CM.getSettings('recaptcha') === true) {
        loadGoogleRecaptchaScript();
      }
    }
  }, [captchaType]);

  useEffect(() => {
    if (captchaType === 'google_recaptcha' && siteKey && isGoogleRecaptchaApiLoaded) {
      const container = document.querySelector('.g-recaptcha');
      if (container && !container.hasAttribute('data-widget-id')) {
          (window as any).grecaptcha.render(container, {
            sitekey: siteKey,
            theme: 'light',
            size: 'normal',
            callback: () => {
              if (onRecaptchaReady) {
                onRecaptchaReady(true);
              }
            }
          });
          
          if (onRecaptchaReady) {
            onRecaptchaReady(true);
          }
      }
    }
  }, [captchaType, siteKey, isGoogleRecaptchaApiLoaded, onRecaptchaReady]);

  const loadGoogleRecaptchaScript = () => {
    // If script already exists, just mark as loaded
    if (document.querySelector('script[src*="google.com/recaptcha/api.js"]')) {
      setIsGoogleRecaptchaApiLoaded(true);
      return;
    }

    // If grecaptcha already exists, mark as loaded
    if (typeof (window as any).grecaptcha !== 'undefined') {
      setIsGoogleRecaptchaApiLoaded(true);
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
    script.async = true;
    script.defer = true;
    
    (window as any).onRecaptchaLoad = () => {
      setIsGoogleRecaptchaApiLoaded(true);
    };
    
    document.head.appendChild(script);
  };

  // Function to load reCAPTCHA when consent is given
  const loadRecaptchaOnConsent = () => {
    loadGoogleRecaptchaScript();
  };

  // Expose function to parent component
  React.useEffect(() => {
    (window as any).loadRecaptchaOnConsent = loadRecaptchaOnConsent;
    return () => {
      delete (window as any).loadRecaptchaOnConsent;
    };
  }, []);

  const loadGoogleRecaptcha = async () => {
    if (!apiUrl) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${apiUrl}contact-form/captcha?type=google_recaptcha`);
      const data = await response.json();
      
      if (data.data && data.data.markup) {
        setCaptchaMarkup(data.data.markup);
        
        const siteKeyMatch = data.data.markup.match(/data-sitekey="([^"]+)"/);
        if (siteKeyMatch) {
          setSiteKey(siteKeyMatch[1]);
        }
      }
    } catch (error) {
      // Handle error silently
    } finally {
      setIsLoading(false);
    }
  };

  const renderMathCaptcha = () => (
    <div className="captchaContainer">
      <span>{captcha.num1}</span>
      <span>+</span>
      <span>{captcha.num2}</span>
      <span>=</span>
      <input
        type="number"
        name="captcha"
        className="form-control"
        value={captcha.result}
        onChange={(e) => onCaptchaChange(e.target.value)}
        placeholder=" "
        required
        style={{ width: '80px', display: 'inline-block' }}
      />
    </div>
  );

  const renderGoogleRecaptcha = () => {
    if (isLoading) {
      return <div className="loading">Loading reCAPTCHA...</div>;
    }
    
    const hasConsent = typeof (window as any).CM !== 'undefined' && (window as any).CM.getSettings('recaptcha') === true;
    
    if (!hasConsent) {
      return (
        <div className="recaptcha-consent-placeholder">
          <div style={{ 
            padding: '20px', 
            border: '1px dashed #ccc', 
            borderRadius: '4px', 
            textAlign: 'center',
            backgroundColor: '#f9f9f9'
          }}>
            <p style={{ color: '#666', marginTop: '0', marginBottom: '0' }}>
              {t('recaptchaConsentPlaceholder')}
            </p>
          </div>
        </div>
      );
    }
    
    if (captchaMarkup) {
      if (captchaMarkup.includes('alert-warning')) {
        return (
          <div 
            className="googleRecaptcha"
            dangerouslySetInnerHTML={{ __html: captchaMarkup }}
          />
        );
      }
      
      if (captchaMarkup.includes('g-recaptcha') && siteKey) {
        return (
          <div className="googleRecaptcha">
            <div 
              className="g-recaptcha" 
              data-sitekey={siteKey}
            />
          </div>
        );
      }
      
      return (
        <div className="error">
          <strong>reCAPTCHA konnte nicht geladen werden</strong><br/>
          <small>Bitte überprüfen Sie die Plugin-Konfiguration oder wählen Sie "Mathematisches Captcha" in den Einstellungen.</small>
        </div>
      );
    }
    
    return <div className="error">Failed to load reCAPTCHA</div>;
  };

  return (
    <div 
      id="form-group-captcha"
      aria-labelledby="form-group-label-captcha"
      className={`form-group ${errors.captcha ? 'has-error' : ''} zr-m-3`}
      role="group"
    >
      <div className="d-flex flex-column-reverse">
        <div className="captchaDisplay">
          {captchaType === 'math' ? renderMathCaptcha() : renderGoogleRecaptcha()}
        </div>
        
        <label 
          id="form-group-label-captcha"
          className="col-form-label pt-0"
        >
          Captcha <span className="text-danger">*</span>
        </label>
      </div>
      {errors.captcha && <div className="form-error-msg">{errors.captcha}</div>}
    </div>
  );
}; 